import { BLOCK_TYPES } from '@model/editor-js/blocks';
import type { BlockType, EditorJsBlock, EditorJsBody } from '@model/editor-js/blocks';

export class EditorJsBodyError extends Error {
    constructor(message: string, readonly path: string) {
        super(`${message} at ${path}`);
        this.name = 'EditorJsBodyError';
    }
}

/** A block type the site has no renderer for; the CMS and the site registries are out of sync. */
export class UnknownBlockTypeError extends EditorJsBodyError {
    constructor(readonly type: unknown, readonly id: string | undefined, path: string) {
        super(`Unknown EditorJS block type "${String(type)}"${id ? ` (block ${id})` : ''}`, path);
        this.name = 'UnknownBlockTypeError';
    }
}

const KNOWN_TYPES: ReadonlySet<string> = new Set(BLOCK_TYPES);

export function isBlockType(type: unknown): type is BlockType {
    return typeof type === 'string' && KNOWN_TYPES.has(type);
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function validateBlocks(blocks: unknown[], path: string): EditorJsBlock[] {
    return blocks.map((block, i) => {
        const at = `${path}[${i}]`;
        if (!isObject(block) || !isObject(block.data)) throw new EditorJsBodyError('Malformed EditorJS block', at);

        const id = typeof block.id === 'string' ? block.id : undefined;
        if (!isBlockType(block.type)) throw new UnknownBlockTypeError(block.type, id, at);

        if (block.type === 'columns') {
            const cols = Array.isArray(block.data.cols) ? block.data.cols : [];
            cols.forEach((col: unknown, c) => {
                const nested = isObject(col) && Array.isArray(col.blocks) ? col.blocks : [];
                validateBlocks(nested, `${at}.cols[${c}].blocks`);
            });
        }
        return block as unknown as EditorJsBlock;
    });
}

/**
 * Turns a stored body into typed blocks.
 *
 * Accepts the JSON string the API returns or an already parsed value, in either envelope:
 * `{ blocks: [...] }` (EditorJS) or a bare array (Word2JSON). Empty input yields no blocks.
 * Throws on invalid JSON, malformed blocks and — deliberately — on unknown block
 * types, so a CMS tool without a renderer fails loudly instead of silently disappearing.
 */
export function parseBody(input: string | EditorJsBody | unknown[] | null | undefined): EditorJsBlock[] {
    if (input === null || input === undefined || input === '') return [];

    let doc: unknown = input;
    if (typeof input === 'string') {
        try {
            doc = JSON.parse(input);
        } catch {
            throw new EditorJsBodyError('Invalid EditorJS JSON', 'body');
        }
    }

    if (Array.isArray(doc)) return validateBlocks(doc, 'blocks');
    if (isObject(doc) && Array.isArray(doc.blocks)) return validateBlocks(doc.blocks, 'blocks');
    throw new EditorJsBodyError('Unexpected EditorJS envelope', 'body');
}

/** Agenda descriptions created before EditorJS are plain text; they render as a single paragraph. */
export function parseBodyOrPlainText(input: string | null | undefined): EditorJsBlock[] {
    if (!input) return [];
    let doc: unknown;
    try {
        doc = JSON.parse(input);
    } catch {
        return [{ type: 'paragraph', data: { text: input } }];
    }
    // plain text that happens to be valid JSON, e.g. "2024"
    if (!isObject(doc) || !Array.isArray(doc.blocks)) return [{ type: 'paragraph', data: { text: input } }];
    return parseBody(doc as unknown as EditorJsBody);
}
